import { Widget } from '../core/Widget.js';
import { createPopper } from '@popperjs/core';
import { DomHelpers } from '../core/DomHelpers.js';

// widgets/MenuWidget.ts
class MenuWidget extends Widget {
    constructor() {
        super(...arguments);
        /** Якорь для позиционирования корня (обычно <label>) */
        this.label = null;
        /** Корневой UL (первый ul внутри root без ul-предка) */
        this.rootMenu = null;
        /** Popper корня */
        this.popperRoot = null;
        /** Все подменю в дереве */
        this.subs = [];
        /** Текущий активный (фокусируемый) элемент действия (a|button|[tabindex]) */
        this.activeAction = null;
        /** Таймер задержки скрытия корня при hover */
        this.rootHideTimer = null;
        // ===== Обработчики корня =====
        this.onClickLabel = (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggleRootWithPosition();
        };
        this.onRootEnter = () => {
            this.clearTimer('root');
            this.showRootAndPosition();
        };
        this.onRootLeave = () => {
            this.clearTimer('root');
            this.rootHideTimer = window.setTimeout(() => this.hideRoot(), this.opts.hoverhidedelay);
        };
        this.onMobileLabelClick = (e) => {
            e.preventDefault();
            this.toggleRoot();
        };
        this.onDocClick = (e) => {
            const t = e.target;
            if (t && !this.root.contains(t))
                this.hideRoot();
        };
        // ===== Вложенные подменю: hover/Popper =====
        /** Наведение на LI — открыть его подменю (и закрыть соседей) */
        this.onLiEnter = (e) => {
            if (this.opts.isMobile())
                return; // mobile не открываем по hover
            const li = e.currentTarget;
            const node = this.subs.find(s => s.li === li);
            if (!node)
                return;
            this.cancelHide(node);
            this.closeSiblings(node);
            this.openSub(node, /*focusFirst*/ false);
        };
        /** Уход с LI — отложить закрытие подменю */
        this.onLiLeave = (e) => {
            if (this.opts.isMobile())
                return;
            const li = e.currentTarget;
            const node = this.subs.find(s => s.li === li);
            if (!node)
                return;
            this.scheduleClose(node);
        };
        /** Наведение на UL подменю — отменить закрытие */
        this.onSubEnter = (e) => {
            const ul = e.currentTarget;
            const node = this.subs.find(s => s.ul === ul);
            if (!node)
                return;
            this.cancelHide(node);
        };
        /** Уход с UL подменю — отложить закрытие */
        this.onSubLeave = (e) => {
            const ul = e.currentTarget;
            const node = this.subs.find(s => s.ul === ul);
            if (!node)
                return;
            this.scheduleClose(node);
        };
        // ===== Клавиатурная навигация (приближенно как в jQuery UI .menu) =====
        /** Обработчик клавиатуры на корне */
        this.onKeyDown = (e) => {
            // работаем только если фокус внутри нашего виджета
            const targetEl = e.target;
            if (!targetEl || !this.root.contains(targetEl))
                return;
            const currentAction = this.findActionFrom(targetEl);
            const currentLi = currentAction?.closest('li');
            const currentUl = currentLi?.parentElement;
            // если меню не показано — игнорируем навигацию
            if (!this.isRootVisible())
                return;
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    if (currentUl)
                        this.focusNextItem(currentUl, currentLi ?? undefined);
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    if (currentUl)
                        this.focusPrevItem(currentUl, currentLi ?? undefined);
                    break;
                case 'Home':
                    e.preventDefault();
                    if (currentUl)
                        this.focusFirstItem(currentUl);
                    break;
                case 'End':
                    e.preventDefault();
                    if (currentUl)
                        this.focusLastItem(currentUl);
                    break;
                case 'ArrowRight': {
                    e.preventDefault();
                    const sub = currentLi?.querySelector(':scope > ul');
                    if (sub) {
                        const node = this.subs.find(s => s.ul === sub);
                        if (node)
                            this.openSub(node, /*focusFirst*/ true);
                    }
                    break;
                }
                case 'ArrowLeft': {
                    e.preventDefault();
                    if (!this.isRootVisible())
                        break;
                    // 1) Если стоим на владельце (currentLi) и его подменю открыто — просто закрываем его
                    if (currentLi) {
                        const nodeOnOwner = this.subs.find(s => s.li === currentLi);
                        if (nodeOnOwner && !DomHelpers.isHidden(nodeOnOwner.ul)) {
                            this.closeSub(nodeOnOwner);
                            const ownerAction = this.getAction(currentLi);
                            if (ownerAction)
                                this.setActive(ownerAction); // вернуть фокус на владельца
                            break;
                        }
                    }
                    // 2) Если мы внутри подменю — закрываем этот уровень и фокусим владельца
                    if (currentUl && currentUl !== this.rootMenu) {
                        const ownerLi = currentUl.parentElement;
                        const node = ownerLi ? this.subs.find(s => s.li === ownerLi) : undefined;
                        if (node)
                            this.closeSub(node);
                        const ownerAction = ownerLi ? this.getAction(ownerLi) : null;
                        if (ownerAction)
                            this.setActive(ownerAction);
                    }
                    else {
                        // 3) На верхнем уровне и ничего не открыто у текущего пункта — закрываем корень
                        this.hideRoot();
                    }
                    break;
                }
                case 'Enter':
                case ' ': {
                    // если есть подменю — открыть, иначе "клик" по пункту
                    if (currentLi) {
                        const sub = currentLi.querySelector(':scope > ul');
                        if (sub) {
                            e.preventDefault();
                            const node = this.subs.find(s => s.ul === sub);
                            if (node)
                                this.openSub(node, /*focusFirst*/ true);
                        }
                        else {
                            const a = this.getAction(currentLi);
                            if (a)
                                a.click(); // естественный клик
                        }
                    }
                    break;
                }
                case 'Escape': {
                    e.preventDefault();
                    // закрыть самое глубокое открытое подменю, либо корень
                    const deepOpen = this.subs.find(s => !DomHelpers.isHidden(s.ul) &&
                        !this.subs.some(o => o.ul !== s.ul && o.ul.contains(s.ul) && !DomHelpers.isHidden(o.ul)));
                    if (deepOpen)
                        this.closeSub(deepOpen);
                    else
                        this.hideRoot();
                    break;
                }
            }
        };
    }
    /** Класс для автоподхвата виджета */
    static _cssKey() { return 'im-ddmenu'; }
    // ===== Инициализация/удаление =====
    /** Инициализация виджета */
    _create(options) {
        this.root = this.element;
        this.label = this.root.querySelector('label');
        this.rootMenu = this.findRootMenu();
        if (!this.rootMenu)
            throw new Error('MenuWidget: корневой <ul> не найден');
        // Итоговые опции
        this.opts = {
            showonclick: options.showonclick ?? false,
            hoverhidedelay: options.hoverhidedelay ?? 300,
            isMobile: options.isMobile ?? (() => document.body.classList.contains('mobile')),
            placement: options.placement ?? 'bottom-start',
            submenuPlacement: options.submenuPlacement ?? 'right-start',
            offset: options.offset ?? [0, 4],
        };
        // Подготовить дерево (ARIA, скрыть всё, построить список подменю)
        this.prepareTree();
        // Режимы
        if (this.opts.isMobile()) {
            this.setupMobile();
        }
        else {
            if (this.opts.showonclick)
                this.setupClickMode();
            else
                this.setupHoverMode();
            this.setupOutsideClose();
        }
        // Навигация клавиатурой (вся внутри root)
        this.root.addEventListener('keydown', this.onKeyDown);
    }
    /** Очистка ресурсов */
    _onDestroy() {
        document.removeEventListener('click', this.onDocClick, true);
        const parent = this.root.parentElement ?? this.root;
        this.label?.removeEventListener('click', this.onClickLabel);
        parent.removeEventListener('mouseenter', this.onRootEnter);
        parent.removeEventListener('mouseleave', this.onRootLeave);
        this.label?.removeEventListener('click', this.onMobileLabelClick);
        this.root.removeEventListener('keydown', this.onKeyDown);
        this.clearTimer('root');
        this.destroyRootPopper();
        this.subs.forEach(s => {
            s.li.removeEventListener('mouseenter', this.onLiEnter);
            s.li.removeEventListener('mouseleave', this.onLiLeave);
            s.ul.removeEventListener('mouseenter', this.onSubEnter);
            s.ul.removeEventListener('mouseleave', this.onSubLeave);
            if (s.action && s.clickHandler) {
                s.action.removeEventListener('click', s.clickHandler);
            }
            if (s.pop) {
                try {
                    s.pop.destroy();
                }
                catch { }
                s.pop = null;
            }
            if (s.hideTimer) {
                clearTimeout(s.hideTimer);
                s.hideTimer = null;
            }
        });
        this.subs = [];
    }
    // ===== Подготовка дерева =====
    /** Поиск корневого UL: первый ul внутри root, у которого нет ul-предка внутри root */
    findRootMenu() {
        const uls = Array.from(this.root.querySelectorAll('ul'));
        for (const ul of uls) {
            const ancestorUl = ul.parentElement?.closest('ul');
            if (!ancestorUl || !this.root.contains(ancestorUl))
                return ul;
        }
        return null;
    }
    /** Настроить ARIA, скрыть все уровни, собрать подменю и проставить tabindex */
    prepareTree() {
        const root = this.rootMenu;
        // Скрыть все списки и проставить роли
        this.root.querySelectorAll('ul').forEach(ul => {
            ul.setAttribute('role', 'menu');
            DomHelpers.hide(ul);
            ul.setAttribute('aria-hidden', 'true');
        });
        // Корень должен быть скрыт — прячем единым способом, без дублирования
        DomHelpers.hide(root);
        root.setAttribute('aria-hidden', 'true');
        // Обозначить пункты и действия
        this.root.querySelectorAll('li').forEach(li => {
            li.setAttribute('role', 'none');
            const action = this.getAction(li);
            if (action) {
                action.setAttribute('role', 'menuitem');
                action.tabIndex = -1; // roving tabindex
            }
        });
        // Собрать подменю: любой :scope > ul
        this.subs = [];
        this.root.querySelectorAll('li').forEach(li => {
            const sub = li.querySelector(':scope > ul');
            if (!sub)
                return;
            // a11y для узла с подменю
            const action = this.getAction(li);
            if (action) {
                action.setAttribute('aria-haspopup', 'menu');
                action.setAttribute('aria-expanded', 'false');
            }
            sub.setAttribute('aria-hidden', 'true');
            DomHelpers.hide(sub);
            const node = { li, ul: sub, action, pop: null, hideTimer: null };
            // Hover для desktop
            li.addEventListener('mouseenter', this.onLiEnter);
            li.addEventListener('mouseleave', this.onLiLeave);
            sub.addEventListener('mouseenter', this.onSubEnter);
            sub.addEventListener('mouseleave', this.onSubLeave);
            // Клик по пункту с подменю — открыть/закрыть, не переходить по ссылке (desktop)
            if (action) {
                const onClick = (e) => {
                    if (!this.opts.isMobile()) {
                        e.preventDefault();
                        e.stopPropagation();
                        const opened = !DomHelpers.isHidden(sub);
                        opened ? this.scheduleClose(node) : this.openSub(node, /*focusFirst*/ true);
                    }
                };
                action.addEventListener('click', onClick);
                node.clickHandler = onClick;
            }
            this.subs.push(node);
        });
        // A11y якоря
        if (this.label) {
            if (!root.id)
                root.id = `im-menu-${Math.random().toString(36).slice(2)}`;
            this.label.setAttribute('aria-controls', root.id);
            this.label.setAttribute('aria-haspopup', 'menu');
            this.label.setAttribute('aria-expanded', 'false');
        }
    }
    /** Вернуть actionable элемент внутри li */
    getAction(li) {
        return li.querySelector('a,button,[tabindex]');
    }
    // ===== Режимы показа корня =====
    /** Desktop: click-режим по явному якорю меню */
    setupClickMode() {
        this.label?.addEventListener('click', this.onClickLabel);
    }
    /** Desktop: hover-режим для корня */
    setupHoverMode() {
        const parent = this.root.parentElement ?? this.root;
        parent.addEventListener('mouseenter', this.onRootEnter);
        parent.addEventListener('mouseleave', this.onRootLeave);
    }
    /** Mobile: простой toggle по клику на label (без Popper) */
    setupMobile() {
        if (!this.label)
            return;
        this.label.addEventListener('click', this.onMobileLabelClick);
    }
    /** Закрытие по клику вне корня */
    setupOutsideClose() {
        document.addEventListener('click', this.onDocClick, true);
    }
    // ===== Показ/скрытие корня + Popper =====
    /** Показать корневое меню и позиционировать относительно label через Popper */
    showRootAndPosition() {
        const root = this.rootMenu;
        DomHelpers.show(root, { display: 'block' });
        root.setAttribute('aria-hidden', 'false');
        this.label?.setAttribute('aria-expanded', 'true');
        // В mobile Popper не нужен (важно для теста и поведения)
        if (!this.opts.isMobile() && this.label) {
            if (!this.popperRoot) {
                this.popperRoot = createPopper(this.label, root, {
                    placement: this.opts.placement,
                    strategy: 'fixed',
                    modifiers: [
                        { name: 'offset', options: { offset: this.opts.offset } },
                        { name: 'flip', options: { fallbackPlacements: ['bottom-end', 'top-start', 'top-end'] } },
                        { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
                    ],
                });
            }
            else {
                this.popperRoot.update();
            }
        }
        // 2026-09-15: открытое меню не выбирает пункт и не забирает фокус — пункт подсвечивается
        // только наведением. Раньше здесь был focusFirstItem: первый пункт оставался подсвеченным,
        // пока курсор стоял на другом.
        this.clearActive();
    }
    /** Скрыть корневое меню и все подменю */
    hideRoot() {
        const root = this.rootMenu;
        // закрыть дочерние подменю
        this.closeAllSubs();
        DomHelpers.hide(root);
        root.setAttribute('aria-hidden', 'true');
        this.label?.setAttribute('aria-expanded', 'false');
        this.destroyRootPopper();
        this.clearActive();
    }
    /** Уничтожить Popper корня */
    destroyRootPopper() {
        if (this.popperRoot) {
            try {
                this.popperRoot.destroy();
            }
            catch { }
            this.popperRoot = null;
        }
    }
    /** Переключить видимость корня с позиционированием */
    toggleRootWithPosition() {
        this.isRootVisible() ? this.hideRoot() : this.showRootAndPosition();
    }
    /** Простой toggle корня (используется в mobile) */
    toggleRoot() {
        this.isRootVisible() ? this.hideRoot() : this.showRootAndPosition();
    }
    /** Видимость корневого меню */
    isRootVisible() {
        const root = this.rootMenu;
        return !DomHelpers.isHidden(root);
    }
    /** Открыть подменю: показать, a11y, Popper; опционально сфокусировать первый пункт */
    openSub(node, focusFirst) {
        DomHelpers.show(node.ul, { display: 'block' });
        node.ul.setAttribute('aria-hidden', 'false');
        node.li.setAttribute('aria-expanded', 'true');
        if (!node.pop) {
            node.pop = createPopper(node.li, node.ul, {
                placement: this.opts.submenuPlacement,
                strategy: 'fixed',
                modifiers: [
                    { name: 'offset', options: { offset: this.opts.offset } },
                    { name: 'flip', options: { fallbackPlacements: ['left-start', 'right-end', 'left-end'] } },
                    { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
                ],
            });
        }
        else {
            node.pop.update();
        }
        if (focusFirst)
            this.focusFirstItem(node.ul);
    }
    /** Поставить таймер закрытия подменю */
    scheduleClose(node) {
        this.cancelHide(node);
        node.hideTimer = window.setTimeout(() => this.closeSub(node), this.opts.hoverhidedelay);
    }
    /** Отменить таймер закрытия подменю */
    cancelHide(node) {
        if (node.hideTimer != null) {
            clearTimeout(node.hideTimer);
            node.hideTimer = null;
        }
    }
    /** Закрыть подменю и всех его потомков */
    closeSub(node) {
        // Сначала закрыть потомков этого узла
        this.subs
            .filter(s => node.ul.contains(s.li))
            .forEach(s => this._hideSubOnly(s));
        // Затем закрыть сам узел
        this._hideSubOnly(node);
    }
    /** Спрятать конкретный узел подменю без рекурсии */
    _hideSubOnly(node) {
        DomHelpers.hide(node.ul);
        node.ul.setAttribute('aria-hidden', 'true');
        node.li.setAttribute('aria-expanded', 'false');
        if (node.pop) {
            try {
                node.pop.destroy();
            }
            catch { }
            node.pop = null;
        }
        this.cancelHide(node);
    }
    /** Закрыть подменю-соседей на том же уровне */
    closeSiblings(node) {
        const parent = node.li.parentElement; // общий UL
        this.subs.forEach(s => {
            if (s === node)
                return;
            if (s.li.parentElement === parent)
                this.closeSub(s);
        });
    }
    /** Закрыть вообще все подменю в дереве */
    closeAllSubs() {
        this.subs.forEach(s => this._hideSubOnly(s));
    }
    /** Найти actionable элемент начиная от узла (поднимаясь вверх) */
    findActionFrom(el) {
        if (el.matches('a,button,[tabindex]'))
            return el;
        const li = el.closest('li');
        return li ? this.getAction(li) : null;
    }
    /** Список фокусируемых пунктов (LI) внутри данного UL */
    itemsOf(ul) {
        const all = Array.from(ul.querySelectorAll(':scope > li'));
        // Корневой UL уже показан через DomHelpers.show → li считаем видимыми,
        // фильтруем только сплиттеры и скрытые явно элементы.
        return all.filter((li) => {
            if (li.classList.contains('menu-splitter'))
                return false;
            if (li.hidden || getComputedStyle(li).display === 'none')
                return false;
            return !!this.getAction(li);
        });
    }
    /** Установить активный пункт (roving tabindex + focus) */
    setActive(actionEl) {
        if (this.activeAction && this.activeAction !== actionEl) {
            this.activeAction.tabIndex = -1;
        }
        this.activeAction = actionEl;
        this.activeAction.tabIndex = 0;
        this.activeAction.focus({ preventScroll: true });
    }
    /** Очистить активный пункт (для скрытия корня) */
    clearActive() {
        if (this.activeAction) {
            this.activeAction.tabIndex = -1;
            this.activeAction = null;
        }
    }
    /** Фокус на первый пункт в UL */
    focusFirstItem(ul) {
        const items = this.itemsOf(ul);
        const act = items.length ? this.getAction(items[0]) : null;
        if (act)
            this.setActive(act);
    }
    /** Фокус на последний пункт в UL */
    focusLastItem(ul) {
        const items = this.itemsOf(ul);
        const act = items.length ? this.getAction(items[items.length - 1]) : null;
        if (act)
            this.setActive(act);
    }
    /** Фокус на следующий пункт в UL (после fromLi, либо с начала) */
    focusNextItem(ul, fromLi) {
        const items = this.itemsOf(ul);
        let idx = fromLi ? items.indexOf(fromLi) : -1;
        idx = Math.min(items.length - 1, idx + 1);
        const act = items[idx] ? this.getAction(items[idx]) : null;
        if (act)
            this.setActive(act);
    }
    /** Фокус на предыдущий пункт в UL (до fromLi, либо на конец) */
    focusPrevItem(ul, fromLi) {
        const items = this.itemsOf(ul);
        let idx = fromLi ? items.indexOf(fromLi) : items.length;
        idx = Math.max(0, idx - 1);
        const act = items[idx] ? this.getAction(items[idx]) : null;
        if (act)
            this.setActive(act);
    }
    // ===== Утилиты =====
    /** Снять таймеры скрытия */
    clearTimer(scope) {
        if (scope === 'root') {
            if (this.rootHideTimer != null) {
                clearTimeout(this.rootHideTimer);
                this.rootHideTimer = null;
            }
            return;
        }
        if (scope.hideTimer != null) {
            clearTimeout(scope.hideTimer);
            scope.hideTimer = null;
        }
    }
}
/** Имя виджета */
MenuWidget._alias = "Menu";

export { MenuWidget };
